import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const INTERSECTION_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1]

const sortByDocumentOrder = (elements: HTMLElement[]): HTMLElement[] =>
  [...elements].sort((a, b) => {
    const pos = a.compareDocumentPosition(b)
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
    return 0
  })

export const useScrollSyncedFloor = (
  visibleFloorCodes: Set<string>,
  scrollRoot: HTMLElement | null,
) => {
  const sectionsRef = useRef<Map<string, HTMLElement>>(new Map())
  const ratiosRef = useRef<Map<string, number>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [activeFloorCode, setActiveFloorCode] = useState<string | null>(null)

  const computeActive = useCallback(() => {
    if (!visibleFloorCodes.size) {
      setActiveFloorCode(null)
      return
    }

    const ordered = sortByDocumentOrder(
      Array.from(sectionsRef.current.entries())
        .filter(([code]) => visibleFloorCodes.has(code))
        .map(([, el]) => el),
    )

    if (!ordered.length) {
      setActiveFloorCode(null)
      return
    }

    let bestCode: string | null = null
    let bestRatio = -1
    let bestIndex = -1

    ordered.forEach((el, index) => {
      const code = el.getAttribute('data-floor-code')
      if (!code) return

      const ratio = ratiosRef.current.get(code) ?? 0
      if (ratio > bestRatio || (ratio === bestRatio && index > bestIndex)) {
        bestRatio = ratio
        bestCode = code
        bestIndex = index
      }
    })

    if (bestRatio > 0 && bestCode) {
      setActiveFloorCode(bestCode)
      return
    }

    const rootRect = scrollRoot?.getBoundingClientRect()
    const scrollCenter = rootRect
      ? rootRect.top + rootRect.height / 2
      : window.innerHeight / 2
    let nearestCode: string | null = null
    let nearestDist = Infinity

    for (const el of ordered) {
      const code = el.getAttribute('data-floor-code')
      if (!code) continue

      const rect = el.getBoundingClientRect()
      const center = rect.top + rect.height / 2
      const dist = Math.abs(center - scrollCenter)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestCode = code
      }
    }

    setActiveFloorCode(nearestCode)
  }, [visibleFloorCodes, scrollRoot])

  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const code = entry.target.getAttribute('data-floor-code')
          if (code) {
            ratiosRef.current.set(code, entry.intersectionRatio)
          }
        }
        computeActive()
      },
      {
        root: scrollRoot,
        threshold: INTERSECTION_THRESHOLDS,
      },
    )

    observerRef.current = observer

    for (const [code, el] of Array.from(sectionsRef.current.entries())) {
      if (visibleFloorCodes.has(code)) {
        observer.observe(el)
      }
    }

    return () => {
      observer.disconnect()
      observerRef.current = null
    }
  }, [visibleFloorCodes, computeActive, scrollRoot])

  useEffect(() => {
    computeActive()
  }, [visibleFloorCodes, computeActive])

  useEffect(() => {
    const root = scrollRoot
    if (!root) return

    const onScroll = () => computeActive()
    root.addEventListener('scroll', onScroll, { passive: true })
    return () => root.removeEventListener('scroll', onScroll)
  }, [scrollRoot, computeActive])

  const registerSection = useCallback(
    (floorCode: string, element: HTMLElement | null) => {
      const observer = observerRef.current
      const existing = sectionsRef.current.get(floorCode)

      if (existing && observer) {
        observer.unobserve(existing)
      }

      if (element) {
        sectionsRef.current.set(floorCode, element)
        ratiosRef.current.set(floorCode, 0)
        if (visibleFloorCodes.has(floorCode) && observer) {
          observer.observe(element)
        }
      } else {
        sectionsRef.current.delete(floorCode)
        ratiosRef.current.delete(floorCode)
      }

      computeActive()
    },
    [visibleFloorCodes, computeActive],
  )

  return { activeFloorCode, registerSection }
}
